import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Phone, Globe, Star, Users, Filter, Download, Trash2, Plus, AlertCircle, CheckCircle, Upload, FileText } from 'lucide-react';
import { googlePlacesService } from './services/googlePlacesService';

interface Prospect {
  id: string;
  name: string;
  address: string;
  phone: string;
  website: string;
  rating: number;
  reviewCount: number;
  isWordPress: boolean;
  wordPressConfidence: 'high' | 'medium' | 'low' | 'unknown';
  hostingProvider: string | null;
  detectionMethods: string[];
  domainAuthority: number;
  monthlyTraffic: number | null;
  trafficEstimate: 'high' | 'medium' | 'low' | 'unknown';
  isExistingCustomer: boolean;
  city: string;
  state: string;
  placeId?: string;
  businessStatus?: string;
  types?: string[];
}

const App = () => {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [existingCustomers, setExistingCustomers] = useState<string[]>([]);
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [filters, setFilters] = useState({
    minRating: 0,
    hasWebsite: false,
    hasPhone: false,
    notCustomer: true
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [newCustomerUrl, setNewCustomerUrl] = useState('');
  const [csvUploadStatus, setCsvUploadStatus] = useState<{
    uploading: boolean;
    message: string;
    type: 'success' | 'error' | 'info' | null;
  }>({ uploading: false, message: '', type: null });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sample data for demonstration
  const sampleProspects: Prospect[] = [
    {
      id: "sample-1",
      name: "Creative Web Solutions",
      address: "123 Main St, Austin, TX",
      phone: "(512) 555-0123",
      website: "https://creativewebsolutions.com",
      rating: 4.5,
      reviewCount: 127,
      isWordPress: true,
      wordPressConfidence: 'high',
      hostingProvider: 'WP Engine',
      detectionMethods: ['HTML: /wp-content/themes/', 'Hosting: WP Engine'],
      domainAuthority: 45,
      monthlyTraffic: 12500,
      trafficEstimate: 'medium',
      isExistingCustomer: false,
      city: "Austin",
      state: "TX"
    },
    {
      id: "sample-2",
      name: "Digital Marketing Pro",
      address: "456 Oak Ave, Austin, TX",
      phone: "(512) 555-0456", 
      website: "https://digitalmarketingpro.com",
      rating: 4.2,
      reviewCount: 89,
      isWordPress: true,
      wordPressConfidence: 'medium',
      hostingProvider: 'SiteGround',
      detectionMethods: ['HTML: /wp-content/', 'Hosting: SiteGround'],
      domainAuthority: 38,
      monthlyTraffic: 8200,
      trafficEstimate: 'low',
      isExistingCustomer: false,
      city: "Austin",
      state: "TX"
    },
    {
      id: "sample-3",
      name: "Austin Web Designs",
      address: "789 Elm St, Austin, TX",
      phone: "(512) 555-0789",
      website: "https://austinwebdesigns.com",
      rating: 4.8,
      reviewCount: 203,
      isWordPress: true,
      wordPressConfidence: 'high',
      hostingProvider: 'Kinsta',
      detectionMethods: ['HTML: /wp-content/themes/', 'WP REST API', 'Hosting: Kinsta'],
      domainAuthority: 52,
      monthlyTraffic: 25800,
      trafficEstimate: 'high',
      isExistingCustomer: true,
      city: "Austin", 
      state: "TX"
    }
  ];

  useEffect(() => {
    // Initialize with sample data
    setProspects(sampleProspects);
  }, []);

  const handleSearch = async () => {
    if (!searchCity.trim()) return;
    
    setLoading(true);
    
    try {
      // Call Google Places API
      const apiResults = await googlePlacesService.searchAgencies(searchCity, searchState);
      
      // Convert ProcessedProspect to Prospect format
      const convertedProspects: Prospect[] = apiResults.map(apiProspect => ({
        ...apiProspect,
        // Check if this prospect is an existing customer
        isExistingCustomer: existingCustomers.includes(apiProspect.website)
      }));
      
      // Set initial results immediately
      setProspects(convertedProspects);
      
      // Switch to results tab if we have prospects
      if (convertedProspects.length > 0) {
        setActiveTab('results');
      }
      
      // Run WordPress detection and traffic estimation in background
      console.log('🔄 Starting background WordPress detection and traffic estimation...');
      
      try {
        const wpResults = await googlePlacesService.batchCheckWordPress(apiResults);
        console.log('✅ WordPress detection completed, starting traffic estimation...');
        
        const finalResults = await googlePlacesService.batchEstimateTraffic(wpResults);
        console.log('✅ Traffic estimation completed, updating prospects...');
        
        const finalProspects: Prospect[] = finalResults.map(apiProspect => ({
          ...apiProspect,
          isExistingCustomer: existingCustomers.includes(apiProspect.website)
        }));
        
        setProspects(finalProspects);
        console.log('✅ All enhancements applied to prospects');
        
      } catch (error) {
        console.error('❌ Background processing failed:', error);
        // Keep the initial results if background processing fails
      }
      
    } catch (error) {
      console.error('Search failed:', error);
      
      // Fallback to sample data if API fails
      const cityResults = sampleProspects.filter(prospect => 
        prospect.city.toLowerCase().includes(searchCity.toLowerCase()) ||
        prospect.state.toLowerCase().includes(searchState.toLowerCase())
      );
      
      setProspects(cityResults);
      
      // Switch to results tab if we have prospects from fallback data
      if (cityResults.length > 0) {
        setActiveTab('results');
      }
      
      // Show user-friendly error message
      alert('Google Places API search failed. Showing sample data instead. Please check your API key configuration.');
    } finally {
      setLoading(false);
    }
  };

  const addExistingCustomer = () => {
    if (!newCustomerUrl.trim()) return;
    
    setExistingCustomers([...existingCustomers, newCustomerUrl.trim()]);
    setNewCustomerUrl('');
    
    // Mark prospects as existing customers if URL matches
    setProspects(prospects.map(prospect => ({
      ...prospect,
      isExistingCustomer: prospect.website === newCustomerUrl.trim() ? true : prospect.isExistingCustomer
    })));
  };

  const removeCustomer = (urlToRemove: string) => {
    setExistingCustomers(existingCustomers.filter(url => url !== urlToRemove));
    
    // Unmark prospects as existing customers
    setProspects(prospects.map(prospect => ({
      ...prospect,
      isExistingCustomer: prospect.website === urlToRemove ? false : prospect.isExistingCustomer
    })));
  };

  const clearAllCustomers = () => {
    if (window.confirm('Are you sure you want to remove all existing customers?')) {
      setExistingCustomers([]);
      
      // Unmark all prospects as existing customers
      setProspects(prospects.map(prospect => ({
        ...prospect,
        isExistingCustomer: false
      })));
      
      setCsvUploadStatus({
        uploading: false,
        message: 'All customers cleared successfully',
        type: 'success'
      });
      
      setTimeout(() => setCsvUploadStatus({ uploading: false, message: '', type: null }), 3000);
    }
  };

  const validateUrl = (url: string): string | null => {
    if (!url || url.trim() === '') return null;
    
    let cleanUrl = url.trim();
    
    // Remove common prefixes that might be in CSV
    cleanUrl = cleanUrl.replace(/^(https?:\/\/)?(www\.)?/, '');
    
    // Add https:// if no protocol
    if (!cleanUrl.startsWith('http')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    // Basic URL validation
    try {
      new URL(cleanUrl);
      return cleanUrl;
    } catch {
      return null;
    }
  };

  const parseCSV = (text: string): string[] => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    const urls: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip header row if it looks like a header
      if (i === 0 && (line.toLowerCase().includes('url') || line.toLowerCase().includes('website') || line.toLowerCase().includes('domain'))) {
        continue;
      }
      
      // Handle CSV format - take first column
      const columns = line.split(',').map(col => col.trim().replace(/^["']|["']$/g, ''));
      const potentialUrl = columns[0];
      
      const validUrl = validateUrl(potentialUrl);
      if (validUrl) {
        urls.push(validUrl);
      }
    }
    
    return urls;
  };

  const handleCSVUpload = async (file: File) => {
    setCsvUploadStatus({ uploading: true, message: 'Processing CSV file...', type: 'info' });
    
    try {
      // Validate file
      if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.txt')) {
        throw new Error('Please upload a CSV or TXT file');
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('File size must be less than 5MB');
      }
      
      // Read file
      const text = await file.text();
      const urls = parseCSV(text);
      
      if (urls.length === 0) {
        throw new Error('No valid URLs found in the file');
      }
      
      // Remove duplicates and filter out existing customers
      const newUrls = urls.filter(url => !existingCustomers.includes(url));
      const duplicateCount = urls.length - newUrls.length;
      
      // Add new URLs
      const updatedCustomers = [...existingCustomers, ...newUrls];
      setExistingCustomers(updatedCustomers);
      
      // Update prospects
      setProspects(prospects.map(prospect => ({
        ...prospect,
        isExistingCustomer: updatedCustomers.includes(prospect.website) ? true : prospect.isExistingCustomer
      })));
      
      // Show success message
      let message = `Successfully imported ${newUrls.length} URLs`;
      if (duplicateCount > 0) {
        message += ` (${duplicateCount} duplicates skipped)`;
      }
      
      setCsvUploadStatus({
        uploading: false,
        message,
        type: 'success'
      });
      
    } catch (error) {
      setCsvUploadStatus({
        uploading: false,
        message: error instanceof Error ? error.message : 'Failed to process CSV file',
        type: 'error'
      });
    }
    
    // Clear status after 5 seconds
    setTimeout(() => setCsvUploadStatus({ uploading: false, message: '', type: null }), 5000);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleCSVUpload(file);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleCSVUpload(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const filteredProspects = prospects.filter(prospect => {
    if (filters.minRating && prospect.rating < filters.minRating) return false;
    if (filters.hasWebsite && !prospect.website) return false;
    if (filters.hasPhone && !prospect.phone) return false;
    if (filters.notCustomer && prospect.isExistingCustomer) return false;
    return true;
  }).sort((a, b) => {
    // Sort by monthly traffic in descending order (highest first)
    // Handle null values by treating them as 0
    const aTraffic = a.monthlyTraffic || 0;
    const bTraffic = b.monthlyTraffic || 0;
    return bTraffic - aTraffic;
  });

  const exportData = () => {
    const exportProspects = filteredProspects.map(prospect => ({
      Name: prospect.name,
      Address: prospect.address,
      Phone: prospect.phone,
      Website: prospect.website,
      Rating: prospect.rating,
      'Review Count': prospect.reviewCount,
      'Domain Authority': prospect.domainAuthority,
      'Uses WordPress': prospect.isWordPress ? 'Yes' : 'No',
      'Existing Customer': prospect.isExistingCustomer ? 'Yes' : 'No'
    }));

    const csvContent = [
      Object.keys(exportProspects[0]).join(','),
      ...exportProspects.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospects_${searchCity}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const ProspectCard = ({ prospect }: { prospect: Prospect }) => {
    const getConfidenceBadge = (confidence: string) => {
      switch (confidence) {
        case 'high': return 'bg-green-100 text-green-800';
        case 'medium': return 'bg-yellow-100 text-yellow-800';
        case 'low': return 'bg-orange-100 text-orange-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
        prospect.isExistingCustomer ? 'border-red-500 bg-red-50' : 'border-blue-500'
      }`}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Lora, serif', color: '#002447' }}>{prospect.name}</h3>
            <div className="flex items-center mt-1">
              <MapPin className="h-4 w-4 text-gray-500 mr-1" />
              <span className="text-sm" style={{ color: '#002447', opacity: 0.8 }}>{prospect.address}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {prospect.isExistingCustomer && (
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">
                Existing Customer
              </span>
            )}
            {prospect.hostingProvider && (
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                {prospect.hostingProvider}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center">
            <Phone className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-sm" style={{ color: '#002447' }}>{prospect.phone || 'No phone'}</span>
          </div>
          <div className="flex items-center">
            <Globe className="h-4 w-4 text-gray-500 mr-2" />
            <a href={prospect.website} target="_blank" rel="noopener noreferrer" 
               className="text-sm hover:underline truncate"
               style={{ color: '#006BD6' }}>
              {prospect.website ? prospect.website.replace('https://', '') : 'No website'}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-4 text-sm mb-3">
          <div className="flex items-center">
            <Star className="h-4 w-4 text-yellow-500 mr-1" />
            <span style={{ color: '#002447' }}>{prospect.rating} ({prospect.reviewCount})</span>
          </div>
        <div title="Domain Authority: SEO ranking score (1-100) indicating website authority and ranking potential">
          <span style={{ color: '#002447', opacity: 0.7 }}>DA: </span>
          <span className="font-medium" style={{ color: '#002447' }}>{prospect.domainAuthority}</span>
        </div>
          <div title="Estimated monthly website traffic">
            <span style={{ color: '#002447', opacity: 0.7 }}>Traffic: </span>
            <span className="font-medium" style={{ color: '#002447' }}>
              {prospect.monthlyTraffic ? `${(prospect.monthlyTraffic / 1000).toFixed(1)}K` : 'Unknown'}
            </span>
          </div>
          <div className="flex items-center">
            {prospect.isWordPress ? (
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
            ) : (
              <AlertCircle className="h-4 w-4 text-gray-400 mr-1" />
            )}
            <span className={prospect.isWordPress ? 'text-green-600' : 'text-gray-500'}>
              WordPress
            </span>
          </div>
          <div title="Web hosting provider">
            <span style={{ color: '#002447', opacity: 0.7 }}>Host: </span>
            <span className="font-medium" style={{ color: '#002447' }}>
              {prospect.hostingProvider || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Enhanced WordPress Detection Info */}
        {prospect.isWordPress && prospect.wordPressConfidence !== 'unknown' && (
          <div className="border-t pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium" style={{ color: '#002447' }}>WordPress Detection:</span>
              <span className={`text-xs font-medium px-2 py-1 rounded ${getConfidenceBadge(prospect.wordPressConfidence)}`}>
                {prospect.wordPressConfidence} confidence
              </span>
            </div>
            {prospect.detectionMethods && prospect.detectionMethods.length > 0 && (
              <div className="text-xs" style={{ color: '#002447', opacity: 0.7 }}>
                <span className="font-medium">Methods: </span>
                {prospect.detectionMethods.slice(0, 2).join(', ')}
                {prospect.detectionMethods.length > 2 && (
                  <span title={prospect.detectionMethods.join(', ')}>
                    {' '}+{prospect.detectionMethods.length - 2} more
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="app-wrapper">
        <div className="mb-8">
          <h1 className="app-title">Agency Prospecting Tool</h1>
          <p className="app-subtitle">Find WordPress advertising agencies by city</p>
        </div>

        {/* Navigation Tabs */}
        <div className="tab-navigation">
          {[
            { id: 'search', name: 'Search Prospects', icon: Search },
            { id: 'customers', name: 'Manage Customers', icon: Users },
            { id: 'urls', name: 'URLs Found', icon: Globe },
            { id: 'results', name: 'Results', icon: Filter }
          ].map(({ id, name, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
            >
              <Icon className="icon" />
              {name}
            </button>
          ))}
        </div>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="search-form-container">
            <h2 className="search-form-title">Search for Agencies</h2>
            
            <div className="search-form-grid">
              <div className="form-field">
                <label className="form-label">City</label>
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="Enter city name"
                  className="form-input"
                />
              </div>
              
              <div className="form-field">
                <label className="form-label">State</label>
                <input
                  type="text"
                  value={searchState}
                  onChange={(e) => setSearchState(e.target.value)}
                  placeholder="Enter state"
                  className="form-input"
                />
              </div>
              
              <div className="search-button-wrapper">
                <button
                  onClick={handleSearch}
                  disabled={loading || !searchCity.trim()}
                  className="btn-primary"
                >
                  {loading ? (
                    <div className="loading-spinner"></div>
                  ) : (
                    <>
                      <Search className="icon" />
                      Search
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="text-sm">
              <p><strong>API Integration:</strong> This app now uses real Google Places API for business discovery:</p>
              <ul className="list-disc list-inside mt-1 space-y-1" style={{ opacity: 0.8 }}>
                <li>✅ Google Places API for finding agencies by location</li>
                <li>✅ Automatic WordPress detection (background process)</li>
                <li>✅ Customer deduplication against your existing customer list</li>
                <li>⚠️ Domain Authority requires separate API integration</li>
              </ul>
              <p className="mt-2" style={{ opacity: 0.8 }}>
                <strong>Setup:</strong> Add your Google Places API key to <code>.env</code> as <code>REACT_APP_GOOGLE_PLACES_API_KEY</code>
              </p>
            </div>
          </div>
        )}

        {/* URLs Tab */}
        {activeTab === 'urls' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Lora, serif', color: '#002447' }}>URLs Found ({prospects.filter(p => p.website).length})</h2>
              <button
                onClick={() => {
                  const urls = prospects.filter(p => p.website).map(p => p.website).join('\n');
                  navigator.clipboard.writeText(urls);
                  alert('URLs copied to clipboard!');
                }}
                disabled={prospects.filter(p => p.website).length === 0}
                className="text-white px-4 py-2 rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                style={{ backgroundColor: prospects.filter(p => p.website).length === 0 ? '#9CA3AF' : '#006BD6' }}
              >
                Copy All URLs
              </button>
            </div>
            
            {prospects.filter(p => p.website).length === 0 ? (
              <div className="text-center py-8">
                <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p style={{ color: '#002447', opacity: 0.7 }}>No URLs found yet. Search for prospects first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {prospects.filter(p => p.website).map((prospect) => (
                  <div key={prospect.id} className={`flex items-center justify-between p-3 rounded border ${
                    prospect.isExistingCustomer ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium min-w-0 flex-1" style={{ color: '#002447' }}>
                          {prospect.name}
                        </span>
                        {prospect.isExistingCustomer && (
                          <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded">
                            Existing Customer
                          </span>
                        )}
                        {prospect.isWordPress && (
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                            WordPress
                          </span>
                        )}
                      </div>
                      <div className="mt-1">
                        <a 
                          href={prospect.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-blue-800 text-sm font-mono break-all"
                          style={{ color: '#006BD6' }}
                        >
                          {prospect.website}
                        </a>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(prospect.website);
                        alert('URL copied!');
                      }}
                      className="ml-4 hover:text-gray-700 text-sm px-2 py-1"
                      style={{ color: '#002447', opacity: 0.7 }}
                      title="Copy URL"
                    >
                      Copy
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 text-sm bg-gray-50 p-4 rounded" style={{ color: '#002447' }}>
              <p><strong>Quick Stats:</strong></p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                <div>Total URLs: <span className="font-medium">{prospects.filter(p => p.website).length}</span></div>
                <div>WordPress Sites: <span className="font-medium">{prospects.filter(p => p.website && p.isWordPress).length}</span></div>
                <div>Existing Customers: <span className="font-medium">{prospects.filter(p => p.website && p.isExistingCustomer).length}</span></div>
                <div>New Prospects: <span className="font-medium">{prospects.filter(p => p.website && !p.isExistingCustomer).length}</span></div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'customers' && (
          <div className="space-y-6">
            {/* Manual Add Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Lora, serif', color: '#002447' }}>Add Individual Customer</h2>
              
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newCustomerUrl}
                  onChange={(e) => setNewCustomerUrl(e.target.value)}
                  placeholder="Enter customer website URL"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue text-navy placeholder-gray-500"
                />
                <button
                  onClick={addExistingCustomer}
                  className="bg-blue text-white px-6 py-3 rounded-lg hover:opacity-90 flex items-center font-medium transition-all duration-200 shadow-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </button>
              </div>
            </div>

            {/* CSV Upload Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Lora, serif', color: '#002447' }}>Upload CSV File</h2>
              
              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2" style={{ color: '#002447' }}>
                  Drop your CSV file here or click to browse
                </p>
                <p className="text-sm" style={{ color: '#002447', opacity: 0.7 }}>
                  Supports CSV and TXT files up to 5MB
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Upload Status */}
              {csvUploadStatus.message && (
                <div className={`mt-4 p-3 rounded-lg ${
                  csvUploadStatus.type === 'success' ? 'bg-green-50 border border-green-200' :
                  csvUploadStatus.type === 'error' ? 'bg-red-50 border border-red-200' :
                  'bg-blue-50 border border-blue-200'
                }`}>
                  <div className="flex items-center">
                    {csvUploadStatus.uploading ? (
                      <div className="loading-spinner mr-3"></div>
                    ) : (
                      <FileText className={`h-5 w-5 mr-3 ${
                        csvUploadStatus.type === 'success' ? 'text-green-600' :
                        csvUploadStatus.type === 'error' ? 'text-red-600' :
                        'text-blue-600'
                      }`} />
                    )}
                    <span className={`text-sm font-medium ${
                      csvUploadStatus.type === 'success' ? 'text-green-800' :
                      csvUploadStatus.type === 'error' ? 'text-red-800' :
                      'text-blue-800'
                    }`}>
                      {csvUploadStatus.message}
                    </span>
                  </div>
                </div>
              )}

              {/* CSV Format Help */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium mb-2" style={{ color: '#002447' }}>Supported CSV Formats:</h3>
                <div className="text-xs space-y-1" style={{ color: '#002447', opacity: 0.8 }}>
                  <p><strong>Simple format:</strong> One URL per line</p>
                  <p><strong>CSV format:</strong> URLs in first column (headers optional)</p>
                  <p><strong>Example:</strong></p>
                  <code className="block mt-1 p-2 bg-white rounded text-xs">
                    https://example1.com<br/>
                    https://example2.com<br/>
                    example3.com
                  </code>
                </div>
              </div>
            </div>

            {/* Customer List Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Lora, serif', color: '#002447' }}>
                  Existing Customers ({existingCustomers.length})
                </h2>
                {existingCustomers.length > 0 && (
                  <button
                    onClick={clearAllCustomers}
                    className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clear All
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {existingCustomers.length === 0 ? (
                  <p className="italic text-center py-8" style={{ color: '#002447', opacity: 0.7 }}>
                    No existing customers added yet. Add individual URLs above or upload a CSV file.
                  </p>
                ) : (
                  existingCustomers.map((url, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm" style={{ color: '#002447' }}>{url}</span>
                      <button
                        onClick={() => removeCustomer(url)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove customer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Lora, serif', color: '#002447' }}>Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#002447' }}>Min Rating</label>
                  <select
                    value={filters.minRating}
                    onChange={(e) => setFilters({...filters, minRating: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                    style={{ color: '#002447', '--tw-ring-color': '#006BD6' } as React.CSSProperties}
                  >
                    <option value={0}>Any Rating</option>
                    <option value={3}>3+ Stars</option>
                    <option value={4}>4+ Stars</option>
                    <option value={4.5}>4.5+ Stars</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasWebsite"
                    checked={filters.hasWebsite}
                    onChange={(e) => setFilters({...filters, hasWebsite: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="hasWebsite" className="text-sm font-medium" style={{ color: '#002447' }}>Has Website</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasPhone"
                    checked={filters.hasPhone}
                    onChange={(e) => setFilters({...filters, hasPhone: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="hasPhone" className="text-sm font-medium" style={{ color: '#002447' }}>Has Phone</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="notCustomer"
                    checked={filters.notCustomer}
                    onChange={(e) => setFilters({...filters, notCustomer: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="notCustomer" className="text-sm font-medium" style={{ color: '#002447' }}>Exclude Customers</label>
                </div>
              </div>
              
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm" style={{ color: '#002447' }}>
                  Showing {filteredProspects.length} of {prospects.length} prospects
                </div>
                <button
                  onClick={exportData}
                  disabled={filteredProspects.length === 0}
                  className="text-white px-4 py-2 rounded-md hover:opacity-90 disabled:bg-gray-400 flex items-center"
                  style={{ backgroundColor: filteredProspects.length === 0 ? '#9CA3AF' : '#006BD6' }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredProspects.length === 0 ? (
                <div className="col-span-2 text-center py-12">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p style={{ color: '#002447', opacity: 0.7 }}>No prospects found. Try adjusting your search or filters.</p>
                </div>
              ) : (
                filteredProspects.map(prospect => (
                  <ProspectCard key={prospect.id} prospect={prospect} />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
